/**
 * Supabase Error Handler
 * Provides utilities for handling Supabase errors, especially 403 (Forbidden) errors
 * related to missing organization assignments or RLS violations.
 */

import { PostgrestError } from '@supabase/supabase-js';
import { toast } from 'sonner';

export interface SupabaseError extends Error {
  code?: string;
  details?: string;
  hint?: string;
  message: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function errorCode(error: unknown): string | undefined {
  if (!isRecord(error) || typeof error.code !== 'string') return undefined;
  return error.code;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (isRecord(error) && typeof error.message === 'string') return error.message;
  return 'Nieznany błąd';
}

function httpStatus(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;
  if (typeof error.status === 'number') return error.status;
  if (typeof error.statusCode === 'number') return error.statusCode;
  return undefined;
}

/**
 * Check if error is a 403 Forbidden error (typically RLS violation or missing org assignment)
 */
export function isForbiddenError(error: unknown): boolean {
  if (!error) return false;

  const message = errorMessage(error);
  return (
    errorCode(error) === 'PGRST301' ||
    httpStatus(error) === 403 ||
    message.includes('permission denied') ||
    message.includes('new row violates row-level security') ||
    message.includes('row-level security policy')
  );
}

/**
 * Check if error is related to missing organization assignment
 */
export function isMissingOrgError(error: unknown): boolean {
  if (!isForbiddenError(error)) return false;

  const message = errorMessage(error).toLowerCase();
  return (
    message.includes('org_id') ||
    message.includes('organization') ||
    message.includes('membership')
  );
}

/**
 * Handle Supabase errors with appropriate user feedback
 * @param error - The error to handle
 * @param context - Optional context for logging (e.g., component name)
 * @param showToast - Whether to show toast notification (default: true)
 * @returns Formatted error message
 */
export function handleSupabaseError(
  error: unknown,
  context?: string,
  showToast: boolean = true
): string {
  if (!error) {
    return 'Nieznany błąd';
  }

  const message = errorMessage(error);
  const logPrefix = context ? `[${context}]` : '[SupabaseErrorHandler]';

  console.error(`${logPrefix} Błąd:`, error);

  if (isForbiddenError(error)) {
    if (isMissingOrgError(error)) {
      const text = 'Brak przypisanej organizacji. Skontaktuj się z administratorem.';
      if (showToast) {
        toast.error(text);
      }
      console.error(`${logPrefix} Brak przypisanej organizacji`);
      return text;
    }
    const text = 'Brak uprawnień do wykonania tej operacji.';
    if (showToast) {
      toast.error(text);
    }
    console.error(`${logPrefix} Brak uprawnień (403)`);
    return text;
  }

  const code = errorCode(error);
  if (code === '23505') {
    const text = 'Rekord o tych danych już istnieje.';
    if (showToast) {
      toast.error(text);
    }
    return text;
  }

  if (code === '23503') {
    const text = 'Nieprawidłowe powiązanie danych.';
    if (showToast) {
      toast.error(text);
    }
    return text;
  }

  if (showToast) {
    toast.error(`Błąd: ${message}`);
  }
  return message;
}

/**
 * Wrapper for Supabase queries that automatically handles errors
 * @param queryFn - Function that returns a Supabase query promise
 * @param context - Optional context for logging
 * @returns Promise with data and error
 */
export async function safeSupabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>,
  context?: string
): Promise<{ data: T | null; error: string | null }> {
  try {
    const result = await queryFn();

    if (result.error) {
      const errorMessageText = handleSupabaseError(result.error, context);
      return { data: null, error: errorMessageText };
    }

    return { data: result.data, error: null };
  } catch (error) {
    const errorMessageText = handleSupabaseError(error, context);
    return { data: null, error: errorMessageText };
  }
}
